import { useEffect, useRef, useState, useCallback } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  ScrollView,
  StyleSheet,
  Dimensions,
  Image,
  Pressable,
  Linking,
  Platform,
  RefreshControl,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { UserSessionAttributes } from '../models/user-session-model';
import { AppDispatch } from '../store';
import SignOutOptions from './SignOutOptions';
import SignInOptions from './SignInOptions';
import UserModeSelect from '../components/UserModeSelect';
import UseVersionCheck from '../components/VersionCheck';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/stackTypes';
import { UserAttributes } from '../models/login-register-model';
import { updateUser } from '../store/actions/user-actions';
import { getUserInfo } from '../services/user-service';
import { isSuccessful } from '../services/api-requests';
import { userActions } from '../store/slices/user-slice';
import { isIPadSync, getResponsiveDimensions, getIPadSafeAreaAdjustments } from '../utils/ipad-utils';
import TestErrorNotifications from '../components/TestErrorNotifications';
import RoleService from '../services/role-service';
import { accessibleTextProps } from '../utils/accessibility-utils';
import { BottomSheetModalRef } from '../components/BottomSheet';

const { width, height } = Dimensions.get('window');

type MyAccountScreenNavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  'MyAccount'
>;

interface MyAccountProps {
  navigation: MyAccountScreenNavigationProp;
}

// Define the type of your popup sheet ref
interface PopupSheetRef {
  open: () => void;
  close: () => void;
}

const highlightColor = '#FFD700'; // gold/yellow
const defaultColor = '#FFFFFF'; // white

const MyAccount = ({ navigation }: MyAccountProps) => {
  const user: UserAttributes = useSelector(
    (state: RootState) => state.user?.userObj
  );
  const userSession: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user?.session
  );
  const dispatch: AppDispatch = useDispatch();
  const [refreshing, setRefreshing] = useState(false);
  const [showRefreshHint, setShowRefreshHint] = useState(false);
  
  // iPad detection and responsive dimensions
  const isIPadDevice = isIPadSync();
  const responsiveDimensions = getResponsiveDimensions();
  const ipadAdjustments = getIPadSafeAreaAdjustments();
  //console.log("Stored state user:", user);
  //const navigation = useNavigation();

  // const handleLogout = async () => {
  //   dispatch(clearSession(userSession));

  //   await AsyncStorage.clear();

  //   const getToken = await AsyncStorage.getItem("token");

  //   navigation.navigate("WelcomeScreen", {
  //     token: getToken,
  //   });
  // };

  const enableHosting = async (enabled: boolean) => {
    await dispatch(
      updateUser(userSession!, {
        host_enabled: enabled,
      })
    );
    const userResponse = await getUserInfo();
    if (isSuccessful(userResponse.status)) {
      dispatch(
        userActions.setUserObj({ userObj: userResponse?.payload?.user })
      );
    }
  };

  // Fetch user info on mount only
  useEffect(() => {
    const getUser = async () => {
      const userResponse = await getUserInfo();
      if (isSuccessful(userResponse.status)) {
        dispatch(
          userActions.setUserObj({ userObj: userResponse.payload?.user })
        );
      }
    };
    getUser();
  }, []);

  // Pull-to-refresh handler
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    setShowRefreshHint(false);
    try {
      const userResponse = await getUserInfo();
      if (isSuccessful(userResponse.status)) {
        dispatch(
          userActions.setUserObj({ userObj: userResponse.payload?.user })
        );
      }
    } catch (error) {
      console.error('Error refreshing user info:', error);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch]);

  // Handle scroll events to show/hide refresh hint
  const handleScroll = useCallback((event: any) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    // Show hint when pulling down (negative offset)
    if (offsetY < -20 && !refreshing) {
      setShowRefreshHint(true);
    } else {
      setShowRefreshHint(false);
    }
  }, [refreshing]);

  const bottomSheetModalRef = useRef<BottomSheetModalRef>(null);
  const bottomSheetSigninRef = useRef<BottomSheetModalRef>(null);

  const OpenSigninSheet = () => {
    if (bottomSheetSigninRef.current) {
      bottomSheetSigninRef.current.present();
    }
  };

  const CloseSigninSheet = () => {
    if (bottomSheetSigninRef.current) {
      bottomSheetSigninRef.current.dismiss();
    }
  };

  const OpenBottomSheet = () => {
    if (bottomSheetModalRef.current) {
      bottomSheetModalRef.current.present();
    }
  };

  const CloseBottomSheet = () => {
    if (bottomSheetModalRef.current) {
      bottomSheetModalRef.current.dismiss();
    }
  };

  const openMeanderWebsite = () => {
    Linking.openURL('https://www.meanderev.com');
  };

  const openDeleteAccountPage = () => {
    Linking.openURL('https://meanderev.com/delete-data/');
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View
        style={[
          styles.container,
          styles.containerBgColor,
          // iPad-specific styling - only apply when on iPad
          isIPadDevice && {
            maxWidth: responsiveDimensions.maxContentWidth,
            alignSelf: 'center',
            width: '100%',
          },
        ]}
      >
        {/* Fixed Header Section */}
        <View className="p-4" style={styles.headerSection}>
          <View className="flex items-center mt-6">
            <Image
              source={require('../../assets/meanderlogowhite_120x120.png')}
              style={{ width: 100, height: 100, alignItems: 'center' }}
            />
            <Text 
              {...accessibleTextProps.label}
              className="text-lg font-semibold text-center text-white"
            >
              {(() => {
                const first = userSession?.first_name ?? 'Guest';
                const last = userSession?.last_name ?? '';
                const isGuest = RoleService.isGuest(userSession);
                // Only add a space if last name exists and not guest
                const name = last
                  ? `${first} ${last}`
                  : first;
                return isGuest ? `${name}\n` : name;
              })()}
            </Text>
            {!RoleService.isGuest(userSession) && (
              <Text
                {...accessibleTextProps.label}
                className="text-sm font-normal text-center"
                style={styles.textColor}
              >{`${userSession?.email ?? ''}`}</Text>
            )}
          </View>
          {userSession && RoleService.isGuest(userSession) ? (
            <View style={styles.line} className="my-4" />
          ) : (
            <View style={{ marginHorizontal: -16 }}>
              <UserModeSelect />
            </View>
          )}
        </View>

        {/* Scrollable Menu Items Section */}
        <ScrollView
          style={styles.scrollViewContent}
          contentContainerStyle={styles.scrollViewContentContainer}
          showsVerticalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor="#FFFFFF"
              colors={["#FFFFFF"]}
            />
          }
        >
          {/* Pull to refresh hint - only shown when pulling down */}
          {showRefreshHint && (
            <View style={styles.refreshHintContainer}>
              <Text style={styles.refreshHintText}>Pull down to refresh</Text>
            </View>
          )}
          
          {/* Test Error Notifications Component - Development Only */}
          {/* {process.env.REACT_NATIVE_ENV === 'development' && (
            <TestErrorNotifications />
          )} */}
          
          {/* Add visual separation when UserModeSelect is hidden (partner-only users) */}
          {userSession && 
           RoleService.hasRole(userSession, 'partner') && 
           !RoleService.hasRole(userSession, 'host') && (
            <View style={styles.topSeparator} />
          )}
          
          <View className="px-4">
            {userSession &&
              RoleService.isPartner(userSession) &&
              userSession?.email !== '' && (
                <Pressable
                  onPress={() => navigation.navigate('PartnerPortal' as any)}
                  style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
                >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <MaterialIcons
                        name="sunny"
                        size={20}
                        color="#3B82F6"
                      />
                      <Text 
                        {...accessibleTextProps.menuItem}
                        className="text-base font-medium"
                        style={{ color: '#3B82F6' }}
                      >
                        Partner Portal
                      </Text>
                    </View>
                    <MaterialIcons
                      name="keyboard-arrow-right"
                      size={20}
                      color="#3B82F6"
                    />
                  </View>
                </Pressable>
              )}
            {userSession && RoleService.isAdmin(userSession) && (
              <Pressable
                onPress={() => navigation.navigate('AdminScreen' as any)}
                style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
              >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <MaterialIcons
                        name="admin-panel-settings"
                        size={20}
                        color="#00D9FF"
                      />
                      <Text 
                        {...accessibleTextProps.menuItem}
                        className="text-base font-medium" 
                        style={{ color: '#00D9FF' }}
                      >
                        Admin Control Panel
                      </Text>
                    </View>
                    <MaterialIcons
                      name="keyboard-arrow-right"
                      size={20}
                      color="white"
                    />
                  </View>
              </Pressable>
            )}
            {userSession && RoleService.hasAnyRole(userSession, ['host', 'admin', 'partner']) && (() => {
              const totalTasks = (user?.onboarding_tasks?.length ?? 0) + (user?.partner_tasks?.length ?? 0);
              const hasOnboardingTasks = (user?.onboarding_tasks?.length ?? 0) > 0;
              const hasPartnerTasks = (user?.partner_tasks?.length ?? 0) > 0;
              const hasTasks = totalTasks > 0;
              
              return (
                <Pressable
                  onPress={() => navigation.navigate('OnboardingStatus')}
                  style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
                >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <MaterialIcons
                        name={
                          hasTasks
                            ? 'assignment'
                            : user?.hostingEnabled
                            ? 'power'
                            : 'power-off'
                        }
                        size={20}
                        color={
                          hasTasks
                            ? highlightColor
                            : defaultColor
                        }
                      />
                      <View style={{ flex: 1 }}>
                        <Text
                          {...accessibleTextProps.menuItem}
                          style={{
                            color:
                              hasTasks
                                ? highlightColor
                                : defaultColor,
                          }}
                          className="text-base font-medium"
                        >
                          {hasTasks
                            ? 'Complete Onboarding'
                            : 'Onboarding Status'}
                        </Text>
                        {hasTasks && (
                          <Text 
                            {...accessibleTextProps.label}
                            className="text-sm text-white opacity-70"
                          >
                            {totalTasks} task
                            {totalTasks > 1 ? 's' : ''}{' '}
                            remaining
                            {hasOnboardingTasks && hasPartnerTasks && ' (host + partner)'}
                            {hasOnboardingTasks && !hasPartnerTasks && ' (host)'}
                            {!hasOnboardingTasks && hasPartnerTasks && ' (partner)'}
                          </Text>
                        )}
                      </View>
                    </View>
                    <MaterialIcons name="chevron-right" size={24} color="white" />
                  </View>
                </Pressable>
              );
            })()}
            {userSession &&
              RoleService.hasAnyRole(userSession, ['host', 'admin']) &&
              userSession?.email !== '' && (
                <Pressable
                  onPress={() => navigation.navigate('MyChargers' as any)}
                  style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
                >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <Image
                        source={require('../../assets/footerIcons/chargers-nav-icon.png')}
                        style={{ width: 20, height: 20, tintColor: '#F59E0B' }}
                      />
                      <Text 
                        {...accessibleTextProps.menuItem}
                        className="text-base font-medium"
                        style={{ color: '#F59E0B' }}
                      >
                        My Chargers
                      </Text>
                    </View>
                    <MaterialIcons
                      name="keyboard-arrow-right"
                      size={20}
                      color="#F59E0B"
                    />
                  </View>
                </Pressable>
              )}
            {userSession &&
              !RoleService.isGuest(userSession) &&
              userSession?.email !== '' && (
                <Pressable
                  onPress={() => navigation.navigate('AccountInfo' as any)}
                  style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
                >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <Image
                        source={require('../../assets/AccountIcon.png')}
                        style={{ width: 20, height: 20 }}
                      />
                      <Text 
                        {...accessibleTextProps.menuItem}
                        className="text-base font-medium text-white"
                      >
                        Update Account Information
                      </Text>
                    </View>
                    <MaterialIcons
                      name="keyboard-arrow-right"
                      size={20}
                      color="white"
                    />
                  </View>
                </Pressable>
              )}
            {userSession &&
              RoleService.hasAnyRole(userSession, ['host', 'admin']) &&
              userSession?.email !== '' && (
                <Pressable
                  onPress={() => navigation.navigate('PayoutRules')}
                  style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
                >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <Image
                        source={require('../../assets/walletWhite.png')}
                        style={{ width: 20, height: 20 }}
                      />
                      <Text 
                        {...accessibleTextProps.menuItem}
                        className="text-base font-medium text-white"
                      >
                        Update Payout Rules
                      </Text>
                    </View>
                    <MaterialIcons
                      name="keyboard-arrow-right"
                      size={20}
                      color="white"
                    />
                  </View>
                </Pressable>
              )}
            {userSession &&
              !RoleService.isGuest(userSession) &&
              userSession?.email !== '' && (
                <Pressable
                  onPress={() => navigation.navigate('TransactionHistory')}
                  style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
                >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <Image
                        source={require('../../assets/transactionIcon.png')}
                        style={{ width: 20, height: 20 }}
                      />
                      <Text 
                        {...accessibleTextProps.menuItem}
                        className="text-base font-medium text-white"
                      >
                        Transaction History
                      </Text>
                    </View>
                    <MaterialIcons
                      name="keyboard-arrow-right"
                      size={20}
                      color="white"
                    />
                  </View>
                </Pressable>
              )}
            {userSession &&
              !RoleService.isGuest(userSession) &&
              userSession?.email !== '' && (
                <Pressable
                  onPress={() =>
                    navigation.navigate('AccountInfo', {
                      changePassword: true,
                    })
                  }
                  style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
                >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <Image
                        source={require('../../assets/LockIcon.png')}
                        style={{ width: 17, height: 20 }}
                      />
                      <Text 
                        {...accessibleTextProps.menuItem}
                        className="text-base font-medium text-white"
                      >
                        Change Password
                      </Text>
                    </View>
                    <MaterialIcons
                      name="keyboard-arrow-right"
                      size={20}
                      color="white"
                    />
                  </View>
                </Pressable>
              )}
            <Pressable
              onPress={openMeanderWebsite}
              style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
            >
              <View className="flex flex-row justify-between items-center my-4">
                <View className="flex flex-row flex-1 gap-3 items-center">
                  <Image
                    source={require('../../assets/infoIcon.png')}
                    style={{ width: 20, height: 20 }}
                  />
                  <Text 
                    {...accessibleTextProps.menuItem}
                    className="text-base font-medium text-white"
                  >
                    About
                  </Text>
                </View>
                <MaterialIcons
                  name="keyboard-arrow-right"
                  size={20}
                  color="white"
                />
              </View>
            </Pressable>
            {userSession &&
              !RoleService.isGuest(userSession) &&
              userSession?.email !== '' && (
                <Pressable
                  onPress={openDeleteAccountPage}
                  style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
                >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <MaterialIcons
                        name="delete-forever"
                        size={20}
                        color="#FF6B6B"
                      />
                      <Text 
                        {...accessibleTextProps.menuItem}
                        className="text-base font-medium text-white"
                      >
                        Delete account and data
                      </Text>
                    </View>
                    <MaterialIcons
                      name="keyboard-arrow-right"
                      size={20}
                      color="white"
                    />
                  </View>
                </Pressable>
              )}
            <Pressable
              onPress={OpenBottomSheet}
              style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
            >
              <View className="flex flex-row justify-between items-center my-4">
                <View className="flex flex-row flex-1 gap-3 items-center">
                  <Image
                    source={require('../../assets/Logout.png')}
                    style={{ width: 20, height: 20 }}
                  />
                  <Text 
                    {...accessibleTextProps.menuItem}
                    className="text-base font-medium text-white"
                  >
                    Logout
                  </Text>
                </View>
                <MaterialIcons
                  name="keyboard-arrow-right"
                  size={20}
                  color="white"
                />
              </View>
            </Pressable>
            {userSession &&
              (RoleService.isGuest(userSession) || userSession?.email === '') && (
                <Pressable
                  onPress={OpenSigninSheet}
                  style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
                >
                  <View className="flex flex-row justify-between items-center my-4">
                    <View className="flex flex-row flex-1 gap-3 items-center">
                      <Image
                        source={require('../../assets/lightning.png')}
                        style={{ width: 20, height: 20 }}
                      />
                      <Text 
                        {...accessibleTextProps.menuItem}
                        className="text-base font-medium text-white"
                      >
                        Login
                      </Text>
                    </View>
                    <MaterialIcons
                      name="keyboard-arrow-right"
                      size={20}
                      color="white"
                    />
                  </View>
                </Pressable>
              )}
          </View>
        </ScrollView>
        <View style={styles.versionContainer}>
          <UseVersionCheck />
        </View>
        <SignOutOptions
          navigation={navigation}
          bottomSheetModalRef={bottomSheetModalRef}
          destination={'WelcomeScreen'}
        />
        <SignInOptions
          navigation={navigation}
          bottomSheetModalRef={bottomSheetSigninRef}
          destination={'MyAccount'}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Platform.OS === 'web' ? '#12121' : '#1A1D21',
  },
  container: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  scrollViewContentContainer: {
    paddingBottom: 100, // Add padding to ensure content doesn't get cut off by footer
    flexGrow: 1, // Ensure content takes full available space
  },
  topSeparator: {
    height: 24, // Visual separation when UserModeSelect is hidden
  },
  refreshHintContainer: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  refreshHintText: {
    color: '#8D8E90',
    fontSize: 12,
    fontStyle: 'italic',
  },
  containerBgColor: {
    backgroundColor: '#1A1D21',
  },
  headerSection: {
    // Fixed header section that doesn't scroll
  },
  versionContainer: {
    paddingBottom: 20, // Add padding for version text
  },
  bgColor: {
    backgroundColor: '#F75555',
  },
  textColor: {
    color: '#8D8E90',
  },
  line: Platform.select({
    web: {
      borderBottomColor: '#636363',
      borderBottomWidth: 1,
      borderStyle: 'dashed',
    },
    default: {
      borderBottomColor: '#636363',
      borderBottomWidth: 1,
    },
  }),
});

export default MyAccount;
